/*
* <license header>
*/

import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Flex, Heading, Text, View } from '@adobe/react-spectrum'
import { PageHeaderBanner } from './shared/PageHeaderBanner'
import { APP_TYPES, APPS } from '../data/constants'
import Compass from '@spectrum-icons/workflow/Compass'

const architectureDiagram = new URL('../assets/architecture-diagram.svg', import.meta.url).href
const processImage = new URL('../assets/process.svg', import.meta.url).href
import FileTemplate from '@spectrum-icons/workflow/FileTemplate'
import Folder from '@spectrum-icons/workflow/Folder'
import Starburst from '@spectrum-icons/workflow/Starburst'
import LinkIcon from '@spectrum-icons/workflow/Link'
import Layers from '@spectrum-icons/workflow/Layers'
import ViewCard from '@spectrum-icons/workflow/ViewCard'
import Code from '@spectrum-icons/workflow/Code'
import App from '@spectrum-icons/workflow/App'
import Globe from '@spectrum-icons/workflow/Globe'
import Gears from '@spectrum-icons/workflow/Gears'
import Graphic from '@spectrum-icons/workflow/Graphic'

const CARD_STYLE = {
  border: '1px solid var(--card-border)',
  borderRadius: '8px',
  padding: '1.25rem',
  backgroundColor: 'var(--white)'
}

const LABEL_STYLE = {
  display: 'inline-block',
  padding: '0.25rem 0.5rem',
  borderRadius: '4px',
  backgroundColor: 'var(--card-accent)',
  color: 'white',
  fontSize: '0.75rem',
  fontWeight: 600,
  marginBottom: '0.5rem'
}

const FEATURE_CARDS = [
  {
    label: `${APP_TYPES.length} App Types`,
    title: 'Application Templates',
    icon: FileTemplate,
    description: 'Pre-built templates for Unified Shell applications, GenStudio extensions, APIs, API Mesh, Workfront extensions, and more. Start with a proven structure and customize to your needs.'
  },
  {
    label: `${APPS.length}+ Apps`,
    title: 'App Repository',
    icon: Folder,
    description: 'Browse and manage your Adobe App Builder applications. Track status, onboard existing apps, and organize your portfolio across the Adobe Experience Cloud ecosystem.'
  },
  {
    label: 'AI Assistant',
    title: 'Ask Assistant',
    icon: Starburst,
    description: 'Get guidance on building apps, configuring extensions, and integrating with Adobe services. AI-powered assistance for development workflows and best practices.'
  },
  {
    label: 'Integrations',
    title: 'Adobe Ecosystem',
    icon: LinkIcon,
    description: 'Connect with Adobe Developer Console, GitHub, and Adobe I/O Runtime. Configure credentials, deploy actions, and streamline your development pipeline.'
  }
]

export const About = () => {
  const [diagramExpanded, setDiagramExpanded] = useState(false)
  const [processExpanded, setProcessExpanded] = useState(false)
  const overlayRef = useRef(null)
  const processOverlayRef = useRef(null)

  useEffect(() => {
    if (diagramExpanded) {
      document.body.style.overflow = 'hidden'
      overlayRef.current?.focus()
      return () => { document.body.style.overflow = '' }
    }
  }, [diagramExpanded])

  useEffect(() => {
    if (processExpanded) {
      document.body.style.overflow = 'hidden'
      processOverlayRef.current?.focus()
      return () => { document.body.style.overflow = '' }
    }
  }, [processExpanded])

  return (
  <View width="100%" minWidth={0} padding="size-400" UNSAFE_style={{ paddingBottom: '2.5rem' }}>
    <PageHeaderBanner
      title="About AppBuilder Factory"
      subtitle="A comprehensive platform for creating and managing Adobe App Builder applications, built on Adobe Experience Cloud."
    />
    <Flex marginBottom="size-300" alignItems="center" gap="size-150">
      <Link
        to="/about/roadmap"
        style={{ color: '#2196f3', fontSize: '0.875rem', textDecoration: 'none', fontWeight: 500 }}
      >
        View Roadmap →
      </Link>
    </Flex>

    <View
      UNSAFE_style={CARD_STYLE}
      marginBottom="size-300"
    >
      <Flex alignItems="center" gap="size-150" marginBottom="size-200">
        <Compass size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
        <Heading level={2} UNSAFE_style={{ margin: 0, fontSize: '1.125rem' }}>
          Our Mission
        </Heading>
      </Flex>
      <Text UNSAFE_style={{ color: 'var(--gray-600)', fontSize: '0.875rem', lineHeight: 1.6 }}>
        AppBuilder Factory transforms how developers create, deploy, and manage Adobe App Builder applications. It provides{' '}
        <strong>ready-to-use templates</strong>, an <strong>AI-powered assistant</strong>, and an <strong>integrated app repository</strong>{' '}
        to accelerate development across the Adobe ecosystem. We help architects, developers, and solution designers build better experiences
        by providing detailed, actionable guidance from initial setup through deployment and beyond.
      </Text>
    </View>

    <View marginBottom="size-300">
      <Flex alignItems="center" gap="size-150" marginBottom="size-400">
        <ViewCard size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
        <Heading level={2} UNSAFE_style={{ margin: 0, fontSize: '1.125rem' }}>
          What&apos;s Included
        </Heading>
      </Flex>
      <div className="CardGrid-wrapper About-sectionContent">
        <div className="CardGrid">
          {FEATURE_CARDS.map((card) => {
            const IconComponent = card.icon
            return (
            <View
              key={card.title}
              UNSAFE_style={CARD_STYLE}
            >
              <span style={LABEL_STYLE}>{card.label}</span>
              <Flex alignItems="center" gap="size-150" marginBottom="size-200">
                <IconComponent size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
                <Heading level={3} UNSAFE_style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
                  {card.title}
                </Heading>
              </Flex>
              <Text UNSAFE_style={{ color: 'var(--gray-600)', fontSize: '0.875rem', lineHeight: 1.5 }}>
                {card.description}
              </Text>
            </View>
            )
          })}
        </div>
      </div>
    </View>

    <View marginBottom="size-300">
      <Flex alignItems="center" gap="size-150" marginBottom="size-400">
        <Layers size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
        <Heading level={2} UNSAFE_style={{ margin: 0, fontSize: '1.125rem' }}>
          Platform Architecture
        </Heading>
      </Flex>
      <Text UNSAFE_style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginBottom: 'size-400', lineHeight: 1.5 }}>
        AppBuilder Factory is built as an Adobe App Builder application with a three-layer architecture:
      </Text>
      <div className="About-sectionContent">
        <Flex direction="column" gap="size-200">
        <View UNSAFE_style={CARD_STYLE}>
          <Flex alignItems="center" gap="size-150" marginBottom="size-200">
            <App size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
            <Heading level={3} UNSAFE_style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
              1. Application Layer (React SPA + Serverless)
            </Heading>
          </Flex>
          <Text UNSAFE_style={{ color: 'var(--gray-600)', fontSize: '0.875rem', lineHeight: 1.5 }}>
            React Spectrum UI in the browser with intelligent routing, content browsing, and an AI-powered chat assistant. Serverless actions on Adobe I/O Runtime (Apache OpenWhisk) handle CRUD for repositories, projects, and workspaces. An MCP Server manages AI assistant conversations and performs actions for the development layer.
          </Text>
        </View>
        <View UNSAFE_style={CARD_STYLE}>
          <Flex alignItems="center" gap="size-150" marginBottom="size-200">
            <Folder size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
            <Heading level={3} UNSAFE_style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
              2. Development Layer (GitHub — Advanced Functionality)
            </Heading>
          </Flex>
          <Text UNSAFE_style={{ color: 'var(--gray-600)', fontSize: '0.875rem', lineHeight: 1.5 }}>
            Customer Instance repositories for manual development and vibe-code workflows. Dev containers with git, Adobe I/O CLI, and MCP Server enable technical users and dev agents to work with Cursor IDE. The AppBuilder Factory repository supports checkout, app creation, agentic development best practices, and check-in to Adobe I/O Customer ORG projects with staging and production workspaces.
          </Text>
        </View>
        <View UNSAFE_style={CARD_STYLE}>
          <Flex alignItems="center" gap="size-150" marginBottom="size-200">
            <Starburst size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
            <Heading level={3} UNSAFE_style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
              3. AI Layer (Common/Basic Functionality)
            </Heading>
          </Flex>
          <Text UNSAFE_style={{ color: 'var(--gray-600)', fontSize: '0.875rem', lineHeight: 1.5 }}>
            MCP Client receives AI assistant conversations and performs actions. An AI Assistant Agent queries RAG (Retrieval-Augmented Generation) for documentation from Adobe Developer Console, Extensions, AppBuilder Factory, and Adobe Spectrum. The LLM Engine generates vibe-coding actions that deploy to the Customer Instance dev container.
          </Text>
        </View>
      </Flex>
      </div>
    </View>

    <View marginBottom="size-300">
      <Flex alignItems="center" gap="size-150" marginBottom="size-400">
        <Graphic size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
        <Heading level={2} UNSAFE_style={{ margin: 0, fontSize: '1.125rem' }}>
          Architecture Design Diagram
        </Heading>
      </Flex>
      <div className="About-sectionContent">
        <div
          className="About-diagramContainer"
          onClick={() => setDiagramExpanded(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setDiagramExpanded(true)}
          aria-label="Click to expand architecture diagram"
        >
          <img
            src={architectureDiagram}
            alt="AppBuilder Factory architecture diagram"
            style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
          />
        </div>
        {diagramExpanded && (
          <div
            ref={overlayRef}
            className="About-diagramOverlay"
            onClick={() => setDiagramExpanded(false)}
            onKeyDown={(e) => e.key === 'Escape' && setDiagramExpanded(false)}
            role="button"
            tabIndex={0}
            aria-label="Close expanded diagram"
          >
            <button
              type="button"
              className="About-diagramClose"
              onClick={() => setDiagramExpanded(false)}
              aria-label="Close"
            >
              ×
            </button>
            <img
              src={architectureDiagram}
              alt="AppBuilder Factory architecture diagram (expanded)"
              className="About-diagramExpanded"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </View>

    <View marginBottom="size-300">
      <Flex alignItems="center" gap="size-150" marginBottom="size-400">
        <Graphic size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
        <Heading level={2} UNSAFE_style={{ margin: 0, fontSize: '1.125rem' }}>
          Process Flow
        </Heading>
      </Flex>
      <div className="About-sectionContent">
        <div
          className="About-diagramContainer About-processDiagram"
          onClick={() => setProcessExpanded(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setProcessExpanded(true)}
          aria-label="Click to expand process flow diagram"
        >
          <img
            src={processImage}
            alt="AppBuilder Factory process flow"
            className="About-processDiagram-img"
          />
        </div>
        {processExpanded && (
          <div
            ref={processOverlayRef}
            className="About-diagramOverlay"
            onClick={() => setProcessExpanded(false)}
            onKeyDown={(e) => e.key === 'Escape' && setProcessExpanded(false)}
            role="button"
            tabIndex={0}
            aria-label="Close expanded diagram"
          >
            <button
              type="button"
              className="About-diagramClose"
              onClick={() => setProcessExpanded(false)}
              aria-label="Close"
            >
              ×
            </button>
            <img
              src={processImage}
              alt="AppBuilder Factory process flow (expanded)"
              className="About-diagramExpanded"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </View>

    <View>
      <Flex alignItems="center" gap="size-150" marginBottom="size-400">
        <Code size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
        <Heading level={2} UNSAFE_style={{ margin: 0, fontSize: '1.125rem' }}>
          Technology Stack
        </Heading>
      </Flex>
      <div className="CardGrid-wrapper About-sectionContent">
        <div className="CardGrid">
          <View UNSAFE_style={CARD_STYLE}>
            <Flex alignItems="center" gap="size-150" marginBottom="size-200">
              <Globe size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
              <Heading level={3} UNSAFE_style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
                Frontend
              </Heading>
            </Flex>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--gray-600)', fontSize: '0.875rem', lineHeight: 1.6 }}>
              <li><strong>React Spectrum 3.4</strong> — Adobe&apos;s design system</li>
              <li><strong>React 16</strong> — Functional components and hooks</li>
              <li><strong>React Router 6</strong> — Client-side routing</li>
              <li><strong>React Error Boundary</strong> — Error handling</li>
              <li><strong>Adobe EXC App</strong> — Experience Cloud Shell integration</li>
              <li><strong>Spectrum Icons</strong> — Workflow icons</li>
            </ul>
          </View>
          <View UNSAFE_style={CARD_STYLE}>
            <Flex alignItems="center" gap="size-150" marginBottom="size-200">
              <Gears size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
              <Heading level={3} UNSAFE_style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
                Backend
              </Heading>
            </Flex>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--gray-600)', fontSize: '0.875rem', lineHeight: 1.6 }}>
              <li><strong>Adobe App Builder</strong> — Enterprise app platform</li>
              <li><strong>Adobe I/O Runtime</strong> — Apache OpenWhisk serverless</li>
              <li><strong>Node.js 18+</strong> — Runtime for actions</li>
              <li><strong>Adobe I/O SDK</strong> — Core libraries and services</li>
              <li><strong>Webpack</strong> — Action bundling</li>
              <li><strong>TypeScript 5.5</strong> — Type safety</li>
            </ul>
          </View>
          <View UNSAFE_style={CARD_STYLE}>
            <Flex alignItems="center" gap="size-150" marginBottom="size-200">
              <LinkIcon size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
              <Heading level={3} UNSAFE_style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
                Integrations
              </Heading>
            </Flex>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--gray-600)', fontSize: '0.875rem', lineHeight: 1.6 }}>
              <li><strong>GitHub</strong> — Repository CRUD and initialization</li>
              <li><strong>Adobe Developer Console</strong> — Project and workspace management</li>
              <li><strong>Adobe I/O Runtime</strong> — Action deployment</li>
              <li><strong>MCP Server</strong> — Model Context Protocol for AI</li>
              <li><strong>CloudEvents</strong> — Event-driven workflows</li>
            </ul>
          </View>
          <View UNSAFE_style={CARD_STYLE}>
            <Flex alignItems="center" gap="size-150" marginBottom="size-200">
              <Starburst size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
              <Heading level={3} UNSAFE_style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
                AI & Development
              </Heading>
            </Flex>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--gray-600)', fontSize: '0.875rem', lineHeight: 1.6 }}>
              <li><strong>RAG</strong> — Retrieval-Augmented Generation</li>
              <li><strong>LLM Engine</strong> — Vibe-coding actions</li>
              <li><strong>Documentation Sources</strong> — Adobe Console, Extensions, Spectrum</li>
              <li><strong>MCP Client</strong> — AI assistant action execution</li>
              <li><strong>Dev Containers</strong> — Git, Adobe I/O CLI</li>
            </ul>
          </View>
        </div>
      </div>
    </View>
  </View>
  )
}
